/**
 * Declarative list filters.
 *
 * A filter is a descriptor for the same reason a column is: every list in the
 * portal needs them, and fifteen bespoke implementations would be fifteen
 * places to get URL round-tripping wrong.
 *
 * Filter state lives in the URL beside search, sort and page, so a filtered
 * view is shareable and the back button returns to it — the same reasoning
 * that put the rest of the list's view state there.
 */

export interface FilterOption {
  readonly value: string;
  readonly label: string;
}

export interface FilterDescriptor {
  readonly id: string;
  readonly label: string;
  /** The URL and API parameter name. */
  readonly param: string;
  /**
   * `entity` is a picker whose values come from an `/options` endpoint.
   *
   * It exists because the other two kinds validate a URL value by membership
   * in `options`, and a picker has no static list to be a member of. A
   * property identifier arriving in the URL was therefore dropped, and the
   * list rendered every row under a control that looked applied — the worst
   * of both, because nothing indicated the filter had been ignored.
   */
  readonly kind: 'select' | 'boolean' | 'entity' | 'text' | 'date';
  /** Required for `select`; the allowlist of acceptable values. */
  readonly options?: readonly FilterOption[];
  /** Required for `entity`; the endpoint its labels are read from. */
  readonly optionsPath?: string;
}

const BOOLEAN_VALUES: readonly string[] = ['true', 'false'];

/**
 * A UUID, in any version.
 *
 * An `entity` value is validated by shape rather than by membership, which is
 * weaker on purpose — the set of acceptable values lives in the database. It
 * is enough: the value only ever reaches the API as a parameter the service
 * parses with `z.uuid()`, so anything shaped wrong is refused twice.
 */
const IDENTIFIER = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Whether a URL value is acceptable for a filter.
 *
 * Validated against the descriptor rather than trusted, because a URL is user
 * input. An unrecognised value is dropped rather than forwarded: sending it on
 * either produces a 422 the reader cannot act on, or is silently ignored by
 * the service and shows an unfiltered list under a filtered-looking control.
 */
function isAcceptable(filter: FilterDescriptor, value: string): boolean {
  if (filter.kind === 'boolean') return BOOLEAN_VALUES.includes(value);
  if (filter.kind === 'entity') return IDENTIFIER.test(value);
  if (filter.kind === 'date') return ISO_DATE.test(value);
  if (filter.kind === 'text') return value.trim() !== '';
  return (filter.options ?? []).some((option) => option.value === value);
}

/** The currently applied filters, keyed by parameter name. */
export function activeFilterValues(
  filters: readonly FilterDescriptor[],
  params: URLSearchParams,
): Readonly<Record<string, string>> {
  const active: Record<string, string> = {};

  for (const filter of filters) {
    const value = params.get(filter.param);
    if (value !== null && value !== '' && isAcceptable(filter, value)) {
      active[filter.param] = value;
    }
  }

  return active;
}

/**
 * The filter parameters to send to the API.
 *
 * Identical to `activeFilterValues` today, and separate on purpose: the list's
 * own parameters (`q`, `page`, `sortBy`) share the same URL, and a single
 * function that returned "everything in the query string" would forward them
 * twice and couple the two concerns.
 */
export function filterParamsFor(
  filters: readonly FilterDescriptor[],
  params: URLSearchParams,
): Readonly<Record<string, string>> {
  return activeFilterValues(filters, params);
}
